import { forwardRef, Fragment, useEffect, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import moment from 'moment';

import { MasterNameApi } from '@/api';
import { FormCard } from '@/components';
import { DATE_FORMAT } from '@/config';
import { useCFSContext, useFetchSasToken, useKaseContext } from '@/hooks';
import { IKaseLog, IMasterName, IProperty, PropertyStatus } from '@/models';
import { useKaseMainContext } from '@/pages/Kases/KaseEdit/KaseMainContext';
import { useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import {
  flattenObject,
  formatPersonName,
  formatPhoneNumber,
  getFileUrlWithValidToken,
  getFormattedDateTime,
  formatPersonNameCanonical,
} from '@/utils';

import { IPropertyLogViewItem, PropertyLogsMap } from './data';
import { PropertyDetailsCard } from './PropertyDetailsCard';
import { SideModalTitle } from './SideModalTitle';
import { useKasePropertyContext } from '../../PropertyContext';

interface CustodyLogsProps {
  isPrint?: boolean;
  isBulk?: boolean;
}

const CustodyLogsComponent = forwardRef<HTMLDivElement, CustodyLogsProps>(
  ({ isPrint, isBulk }, ref) => {
    const { updateToast } = useToastStore();
    const { currentPropertyId, custodyModalValues, parentId, sourceAdapter } =
      useKasePropertyContext();
    const { kase } = useKaseContext();
    const { cfs } = useCFSContext();
    const { PropertyType } = useKaseMainContext();

    const getPropertyTypeLabel = (typeKey?: string) => {
      if (!typeKey || !PropertyType?.size) return undefined;
      const allPropertyTypes = Array.from(PropertyType.values());
      return allPropertyTypes.find((value) => value.key === typeKey)?.label;
    };
    const [custodyLogs, setCustodyLogs] = useState<IKaseLog[]>([]);
    const [masterNames, setMasterNames] = useState<IMasterName[]>([]);
    const [propertiesMap, setPropertiesMap] = useState<Map<string, IProperty>>(
      new Map(),
    );
    const [loading, setLoading] = useState<boolean>(true);

    const sasToken = useFetchSasToken(
      custodyLogs.map(
        (log) => log.data?.new?.custodyActionResultInfo?.signature,
      ),
    );

    useEffect(() => {
      fetchCustodyLogs();
    }, [currentPropertyId, parentId, isBulk]);

    useEffect(() => {
      const ownerIds = Array.from(
        new Set(
          custodyLogs
            .filter(
              (log) =>
                log.data.new?.status === PropertyStatus.RELEASED_TO_OWNER,
            )
            .map((log) => log.data.new?.owner)
            .filter((ownerId): ownerId is string => Boolean(ownerId)),
        ),
      );

      if (!ownerIds.length) {
        setMasterNames([]);
        return;
      }

      fetchMasterNames(ownerIds);
    }, [custodyLogs]);

    useEffect(() => {
      const propertyIds = Array.from(
        new Set(
          custodyLogs
            .map((log) => log.linkedItems?.property)
            .filter((id): id is string => Boolean(id)),
        ),
      );

      if (!propertyIds.length || !parentId) {
        setPropertiesMap(new Map());
        return;
      }

      fetchProperties(propertyIds);
    }, [custodyLogs, parentId]);

    const fetchProperties = async (propertyIds: string[]) => {
      try {
        const results = await Promise.all(
          propertyIds.map((id) => sourceAdapter.getOne(parentId, id)),
        );
        const map = new Map<string, IProperty>();
        results.forEach((res) => {
          const property = res.data;
          if (property?._id) {
            map.set(property._id, property);
          }
        });
        setPropertiesMap(map);
      } catch (error: any) {
        updateToast({ open: true, message: error.message });
      }
    };

    const fetchMasterNames = async (ownerIds: string[]) => {
      try {
        const result = await MasterNameApi.getMasterNames({
          ids: ownerIds.join(','),
        });
        setMasterNames(result.data);
      } catch (error: any) {
        updateToast({ open: true, message: error.message });
      }
    };

    const fetchCustodyLogs = async () => {
      if (!parentId) return;
      const { bulkSelection } = custodyModalValues;
      const hasBulkSelection = Boolean(bulkSelection?.length);

      setLoading(true);
      try {
        const res = await sourceAdapter.getCustodyLogs(
          parentId,
          // If bulk selection or 'isBulk' is true, we don't need to send a property ID to the API.
          // We only send 'currentPropertyId' for a single property request.
          hasBulkSelection || isBulk ? undefined : currentPropertyId,
        );

        if (hasBulkSelection) {
          // If 'bulkSelection' is defined, filter the logs based on the selected properties.
          const filteredCustodyLogs = res.data.results.filter((log) => {
            return (
              log.linkedItems?.property &&
              bulkSelection?.includes(log.linkedItems?.property)
            );
          });
          setCustodyLogs(filteredCustodyLogs);
        } else {
          // This part prints either bulk print (all) or single property custody logs.
          setCustodyLogs(res.data.results);
        }
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      } finally {
        setLoading(false);
      }
    };

    const renderLogViewItem = (
      item: IPropertyLogViewItem,
      data: Record<string, any>,
    ) => {
      const logData = flattenObject(data.new?.custodyActionResultInfo);
      let value = logData[item.key];

      if (item.key === 'releasedTo') {
        const custodyInfo = data.new?.custodyActionResultInfo;
        value =
          formatPersonName(custodyInfo?.user) ||
          (custodyInfo?.masterName
            ? formatPersonNameCanonical(custodyInfo.masterName)
            : '');
      }
      if (item.type === 'officer') {
        const fullname = formatPersonName({
          firstName: logData['officer.firstName'],
          middleName: logData['officer.middleName'],
          lastName: logData['officer.lastName'],
        });
        value = fullname || logData['officer.email'];
      }
      if (item.type === 'date') {
        value = getFormattedDateTime(value);
      }
      if (item.key.toLowerCase().includes('phonenumber')) {
        value = formatPhoneNumber(value);
      }
      if (item.key === 'signature') {
        const signatureFile = data.new?.custodyActionResultInfo?.signature;
        if (!signatureFile) return null;
        const signatureUrl = getFileUrlWithValidToken(signatureFile, sasToken);

        value = <img src={`${signatureUrl}`} alt={signatureFile.name} />;
      }
      if (item.key.toLowerCase() === 'expectedbackdate') {
        const parsedDate = moment(value);
        value = parsedDate.isValid() ? parsedDate.format(DATE_FORMAT) : null;
      }
      if (item.key === 'owner') {
        const masterName = masterNames.find(
          (owner: IMasterName) => owner._id === data.new.owner,
        );

        value = masterName
          ? formatPersonNameCanonical(masterName)
          : 'Owner not found';
      }
      if (value == null || value === '') return null;
      return value;
    };

    const renderStructuredField = (
      item: IPropertyLogViewItem,
      data: Record<string, any>,
    ) => {
      const value = renderLogViewItem(item, data);

      if (value == null) return null;

      return (
        <Box
          key={item.key}
          sx={{
            minWidth: 0,
            px: 2,
            py: 1.5,
            gridColumn:
              item.key === 'notes' || item.key === 'signature'
                ? '1 / -1'
                : undefined,
          }}
        >
          <Typography fontSize={14} fontWeight={500}>
            {item.label}
          </Typography>
          <Typography
            fontSize={14}
            sx={{
              wordBreak: 'break-word',
              '& img': {
                display: 'block',
                maxWidth: '100%',
                maxHeight: 140,
                objectFit: 'contain',
              },
            }}
          >
            {value}
          </Typography>
        </Box>
      );
    };

    // Group by property number
    const groupedCustodyLogs: Record<string, IKaseLog[]> = custodyLogs.reduce(
      (groups: Record<string, IKaseLog[]>, log) => {
        const propertyNumber = log.data.new?.custodyActionResultInfo?.property
          .number as string;
        if (!groups[propertyNumber]) {
          groups[propertyNumber] = [];
        }
        groups[propertyNumber].push(log);
        return groups;
      },
      {} as Record<string, IKaseLog[]>,
    );

    return (
      <Stack
        style={{
          height: `calc(100vh - ${isPrint ? '160px' : '85px'})`,
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: 5,
          paddingBottom: 5,
        }}
      >
        {!isPrint && <SideModalTitle>View Custody Log</SideModalTitle>}
        <Stack
          ref={ref}
          style={{
            padding: 4,
            margin: 2,
          }}
        >
          {Object.entries(groupedCustodyLogs).map(([propertyNumber, logs]) => {
            const propertyId = logs[0]?.linkedItems?.property;
            const property = propertyId
              ? propertiesMap.get(propertyId)
              : undefined;

            return (
              <Fragment key={propertyNumber}>
                {property ? (
                  <PropertyDetailsCard
                    property={property}
                    caseId={kase?.number}
                    cfsId={cfs?.number}
                    propertyTypeLabel={getPropertyTypeLabel(property.type)}
                  />
                ) : (
                  <Typography
                    variant="inherit"
                    sx={{
                      fontWeight: 500,
                      marginBottom: 1,
                      color: colors.grey[100],
                    }}
                  >
                    {propertyNumber}
                  </Typography>
                )}

                {logs?.length &&
                  logs.map((log) => {
                    const { data, createdAt } = log;
                    const viewFields =
                      PropertyLogsMap[data.new?.status as PropertyStatus];
                    const key = `${data.new?.status}-${createdAt}`;
                    const officer = data.new?.custodyActionResultInfo?.officer;

                    const officerName = formatPersonName(officer);
                    return (
                      <FormCard
                        key={key}
                        title={
                          <Stack
                            className="cardTitle"
                            style={{ padding: '5px 10px 0' }}
                            sx={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="inherit" component="span">
                              {getFormattedDateTime(
                                new Date(createdAt).toISOString(),
                              )}
                              {officerName && ` - ${officerName}`}
                            </Typography>
                            <Typography variant="inherit" component="span">
                              {viewFields.statusLabel}
                            </Typography>
                          </Stack>
                        }
                        style={{ fontWeight: 500 }}
                        sx={{
                          '& .divider': {
                            marginTop: '5px',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            gridColumn: '1 / -1',
                            display: 'grid',
                            gap: 0,
                            width: '100%',
                            gridTemplateColumns: {
                              xs: '1fr',
                              md: 'repeat(3, minmax(0, 1fr))',
                            },
                          }}
                        >
                          {viewFields.items?.map((item) =>
                            renderStructuredField(item, data),
                          )}
                        </Box>
                      </FormCard>
                    );
                  })}
              </Fragment>
            );
          })}

          {!loading && !custodyLogs?.length && (
            <Typography variant="body1">There are no logs available</Typography>
          )}
        </Stack>
      </Stack>
    );
  },
);
CustodyLogsComponent.displayName = 'CustodyLogs';

export const CustodyLogs = CustodyLogsComponent;
